import {isFunction} from '../../util/function';

/**
 * Higher-order component that allows you to compose multiple components into a single component
 * while preserving reactivity, access to magics, access to the parent scope, and calling each component's `init()` method
 *
 * @example
 *
 * <div x-data="compose(componentA, componentB)">
 *  <span x-text="somePropFromComponentA"></span>
 *  <span x-text="somePropFromComponentB"></span>
 * </div>
 *
 * @param components
 * @returns
 */
export default function compose(...components) {
  return components.reduce(
    (composedComponent, componentOrComponentFunction) => {
      const component = isFunction(componentOrComponentFunction) ? componentOrComponentFunction() : componentOrComponentFunction;

      // Add the init method to the list of initializers to be called in order once composed component is initialized
      if (component.init) {
        composedComponent._initializers.push(component.init);
      }

      // Delete the init method so it doesn't get called twice
      delete component.init;

      // Add property descriptors from component to composed component
      const extendedComponent = Object.defineProperties(composedComponent, Object.getOwnPropertyDescriptors(component));

      return extendedComponent;
    },
    {
      _initializers: [],
      init() {
        // Call each initializer in order
        this._initializers.forEach((initializer) => initializer.call(this));
      },
    }
  );
}
